import { useCallback } from 'react';
import type { Editor } from '@tiptap/react';

import { DocumentVariables, resolveDocumentTokens } from 'containers/Tiptap/lib/header-footer-variables';
import { RENDERED_BAND_SELECTOR, getHeaderFooterRoot } from 'containers/Tiptap/lib/header-footer-dom';
import { useHeaderFooterDomPatch } from 'containers/Tiptap/hooks/use-header-footer-dom-patch';

const resolvedText = new WeakMap<Text, { template: string; resolved: string }>();

// Pages resolves `{page}`/`{total}` itself but leaves the document-level tokens as text.
export function useResolveHeaderFooterVariables(editor: Editor | null, variables: DocumentVariables): void {
  const { documentName, documentVersion } = variables;

  const apply = useCallback((): void => {
    if (!editor) return;
    getHeaderFooterRoot(editor).querySelectorAll(RENDERED_BAND_SELECTOR).forEach((band) => {
      const walker = document.createTreeWalker(band, NodeFilter.SHOW_TEXT);
      const textNodes: Text[] = [];
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        textNodes.push(node as Text);
      }
      textNodes.forEach((textNode) => {
        const text = textNode.nodeValue ?? '';
        const previous = resolvedText.get(textNode);
        const template = previous && previous.resolved === text ? previous.template : text;
        const replaced = resolveDocumentTokens(template, { documentName, documentVersion });
        if (replaced === template) {
          resolvedText.delete(textNode);
          return;
        }
        resolvedText.set(textNode, { template, resolved: replaced });
        if (replaced !== text) textNode.nodeValue = replaced;
      });
    });
  }, [editor, documentName, documentVersion]);

  useHeaderFooterDomPatch(editor, apply);
}
